import React from "react";
import { Grid, GridColumn, GridToolbar } from "@progress/kendo-react-grid";
import { ExcelExport } from "@progress/kendo-react-excel-export";
import { DropDownList } from "@progress/kendo-react-dropdowns";
import { Button } from "@progress/kendo-react-buttons";
import { process } from "@progress/kendo-data-query";

export default class AdminUsersGrid extends React.Component {
  constructor(props) {
    super(props);
    this._export = null;
    this.state = {
      data: (this.props.users || []).slice(),
      dataState: {
        skip: 0,
        take: 20,
        sort: [{ field: "name", dir: "asc" }]
      },
      filters: {
        role: null,
        status: null,
        search: ""
      }
    };
  }

  onDataStateChange = (e) => {
    this.setState({ dataState: e.dataState });
  };

  getFilteredData = () => {
    var data = this.state.data;
    var f = this.state.filters;

    if (f.role) {
      data = data.filter(d => d.role === f.role);
    }
    if (f.status !== null) {
      data = data.filter(d => d.isApproved === f.status);
    }
    if (f.search) {
      var s = f.search.toLowerCase();
      data = data.filter(d =>
        (d.name || "").toLowerCase().indexOf(s) > -1 ||
        (d.email || "").toLowerCase().indexOf(s) > -1 ||
        (d.company || "").toLowerCase().indexOf(s) > -1
      );
    }
    return data;
  };

  onFilterChange = (field, value) => {
    this.setState(prev => ({
      filters: { ...prev.filters, [field]: value },
      dataState: { ...prev.dataState, skip: 0 }
    }));
  };

  onRefresh = () => {
    this.setState({
      dataState: {
        skip: 0,
        take: 20,
        sort: [{ field: "name", dir: "asc" }]
      },
      filters: {
        role: null,
        status: null,
        search: ""
      }
    });
  };

  exportExcel = () => {
    if (this._export) {
      this._export.save(this.getFilteredData());
    }
  };

  toggleUserStatus = (userId, currentStatus) => {
    var newStatus = !currentStatus;
    fetch("/api/Admin/ToggleUserStatus", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ userId: userId, isActive: newStatus })
    })
      .then(res => {
        if (res.ok) {
          this.setState(prev => ({
            data: prev.data.map(u =>
              u.id === userId ? { ...u, isApproved: newStatus } : u
            )
          }));
        }
      });
  };

  updateUserRole = (userId, newRole) => {
    fetch("/api/Admin/UpdateUserRole", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ userId: userId, role: newRole })
    })
      .then(res => {
        if (res.ok) {
          this.setState(prev => ({
            data: prev.data.map(u =>
              u.id === userId ? { ...u, role: newRole } : u
            )
          }));
        }
      });
  };

  impersonateUser = (userId, userEmail) => {
    if (!confirm("Sign in as " + userEmail + "?\n\nYou will be signed out of your admin account and signed in as this user. A banner at the top of every page will let you switch back.")) return;
    fetch("/api/Admin/ImpersonateUser", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ userId: userId })
    })
      .then(function (res) { return res.json().then(function (j) { return { ok: res.ok, body: j }; }); })
      .then(function (r) {
        if (r.ok) { window.location.href = "/"; }
        else { alert(r.body.message || "Impersonation failed"); }
      })
      .catch(function (e) { alert("Error: " + e.message); });
  };

  updateUserSalesRep = (userId, newSalesRep) => {
    fetch("/api/Admin/UpdateUserSalesRep", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ userId: userId, salesRep: newSalesRep })
    })
      .then(res => {
        if (res.ok) {
          this.setState(prev => ({
            data: prev.data.map(u =>
              u.id === userId ? { ...u, salesRep: newSalesRep } : u
            )
          }));
        }
      });
  };

  render() {
    var filteredData = this.getFilteredData();
    var result = process(filteredData, this.state.dataState);
    var roleOptions = this.props.roleOptions || [];
    // Read-only mode (Staff role, ticket 86e3rhvnj): mutation controls
    // render as plain text. The backing APIs are Administrator-only
    // server-side regardless.
    var readOnly = this.props.readOnly === true;
    var salesRepOptions = (this.props.salesRepOptions || []).map(s => s.email);
    var statusOptions = [
      { text: "Active", value: true },
      { text: "Disabled", value: false }
    ];

    return (
      <div>
        <div className="quote-history-filters">
          <div className="quote-filter-item">
            <label>Role</label>
            <DropDownList
              data={roleOptions}
              value={this.state.filters.role}
              onChange={(e) => this.onFilterChange("role", e.target.value)}
              defaultItem="All Roles"
              style={{ width: "180px" }}
            />
          </div>
          <div className="quote-filter-item">
            <label>Status</label>
            <DropDownList
              data={statusOptions}
              textField="text"
              dataItemKey="value"
              value={this.state.filters.status !== null
                ? statusOptions.find(s => s.value === this.state.filters.status)
                : null}
              onChange={(e) => {
                var val = e.target.value;
                this.onFilterChange("status", val && val.value !== undefined ? val.value : null);
              }}
              defaultItem={{ text: "All Statuses", value: null }}
              style={{ width: "160px" }}
            />
          </div>
          <div className="quote-filter-item">
            <label>Search</label>
            <input
              type="text"
              placeholder="Name, email, or company..."
              value={this.state.filters.search}
              onChange={(e) => this.onFilterChange("search", e.target.value)}
              style={{ padding: "8px 12px", border: "1px solid #ccc", borderRadius: "4px", fontSize: "14px", width: "260px" }}
            />
          </div>
        </div>

        <ExcelExport ref={(exp) => { this._export = exp; }} fileName="Users.xlsx">
          <Grid
            data={result}
            {...this.state.dataState}
            onDataStateChange={this.onDataStateChange}
            sortable={true}
            pageable={{ pageSizes: [10, 20, 50] }}
            cellRender={(cell, cellProps) => {
              if (!cell || !cell.props) return cell;
              const labels = { name: "Name", email: "Email", company: "Company", role: "Role", isApproved: "Status", salesRep: "Sales Rep", lastActivityDate: "Last Activity", createdAt: "Created" };
              const label = labels[cellProps.field] || "";
              return React.cloneElement(cell, { ...cell.props, "data-label": label });
            }}
          >
            <GridToolbar>
              <button className="ui-button secondary min-width" onClick={this.exportExcel}
                style={{ background: "#333", color: "#fff", border: "none", padding: "8px 16px", cursor: "pointer", fontSize: "13px" }}>
                Export to Excel
              </button>
              <button className="ui-button secondary min-width" onClick={this.onRefresh}
                style={{ marginLeft: "auto", padding: "8px 16px", cursor: "pointer", fontSize: "13px" }}>
                Refresh
              </button>
            </GridToolbar>
            <GridColumn field="name" title="Name" width="180px"
              cell={(props) => (
                <td data-label="Name">
                  <a href={"/Admin/Users/" + encodeURIComponent(props.dataItem.id)}
                     style={{ color: "#333", textDecoration: "underline" }}>
                    {props.dataItem.name}
                  </a>
                </td>
              )}
            />
            <GridColumn field="email" title="Email" width="220px" />
            <GridColumn field="company" title="Company" width="200px" />
            <GridColumn field="role" title="Role" width="150px"
              cell={(props) => (
                <td data-label="Role">
                  {readOnly ? (props.dataItem.role || "") : (
                    <DropDownList
                      data={roleOptions}
                      value={props.dataItem.role}
                      onChange={(e) => this.updateUserRole(props.dataItem.id, e.target.value)}
                      style={{ width: "130px", fontSize: "13px" }}
                    />
                  )}
                </td>
              )}
            />
            <GridColumn field="isApproved" title="Status" width="110px"
              cell={(props) => (
                <td data-label="Status">
                  {readOnly ? (
                    <span className={"admin-status-toggle " + (props.dataItem.isApproved ? "active" : "disabled")}
                          style={{ cursor: "default" }}>
                      {props.dataItem.isApproved ? "Active" : "Disabled"}
                    </span>
                  ) : (
                    <button
                      onClick={() => this.toggleUserStatus(props.dataItem.id, props.dataItem.isApproved)}
                      className={"admin-status-toggle " + (props.dataItem.isApproved ? "active" : "disabled")}
                    >
                      {props.dataItem.isApproved ? "Active" : "Disabled"}
                    </button>
                  )}
                </td>
              )}
            />
            <GridColumn field="salesRep" title="Sales Rep" width="220px"
              cell={(props) => (
                <td data-label="Sales Rep">
                  {readOnly ? (props.dataItem.salesRep || "") : (
                    <DropDownList
                      data={salesRepOptions}
                      value={props.dataItem.salesRep || ""}
                      onChange={(e) => this.updateUserSalesRep(props.dataItem.id, e.target.value)}
                      defaultItem=""
                      style={{ width: "200px", fontSize: "13px" }}
                    />
                  )}
                </td>
              )}
            />
            <GridColumn field="lastActivityDate" title="Last Activity" width="110px" />
            <GridColumn field="createdAt" title="Created" width="100px"
              cell={(props) => (
                <td title={props.dataItem.createdAt} data-label="Created">{props.dataItem.createdAt}</td>
              )}
            />
            {!readOnly && (
              <GridColumn title="Actions" width="120px" sortable={false}
                cell={(props) => {
                  var role = (props.dataItem.role || "").toLowerCase();
                  var isAdmin = role === "administrator";
                  return (
                    <td data-label="Actions">
                      {!isAdmin && (
                        <Button
                          size="small"
                          onClick={() => this.impersonateUser(props.dataItem.id, props.dataItem.email)}
                          title={"Sign in as " + props.dataItem.email}
                        >
                          Login As
                        </Button>
                      )}
                    </td>
                  );
                }}
              />
            )}
          </Grid>
        </ExcelExport>
      </div>
    );
  }
}
